/* eslint-disable import/no-restricted-paths */
import { useEffect, useRef, useState } from "react";

import { Loader } from "@/components/Loader";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import usePdfToImages from "@/features/request/feedback-tool/feedbackTool/hooks/usePdfToImages";
import { usePdfPinchZoom } from "@/features/request/feedback-tool/feedbackTool/pdf/hooks/usePdfPinchZoom";
import {
  clampZoom,
  usePdfWheelZoom,
} from "@/features/request/feedback-tool/feedbackTool/pdf/hooks/usePdfWheelZoom";

import "./style.css";

export default function PdfViewer({
  attachmentId,
  maxZoom,
  onZoomChange,
  url,
  zoom,
}: {
  attachmentId: number;
  maxZoom: number;
  onZoomChange: (zoom: number) => void;
  url: string;
  zoom: number;
}) {
  const [page, setPage] = useState(1);
  const { imageUrls } = usePdfToImages({ attachmentId, pdfUrl: url });
  const { isTablet, pdfContainerRef } = useFeedbackTool();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const imgRefs = useRef<Map<string, HTMLImageElement>>(new Map());
  const [fitWidth, setFitWidth] = useState<null | number>(null);
  const zoomRef = useRef(zoom);

  zoomRef.current = zoom;

  usePdfPinchZoom({
    contentRef: pdfContainerRef,
    enabled: isTablet,
    hScrollerRef: scrollerRef,
    maxWidth: fitWidth ? fitWidth * maxZoom : null,
    minWidth: fitWidth,
    onZoomEnd: (width) => {
      if (!fitWidth) return;

      onZoomChange(clampZoom(width / fitWidth, maxZoom));
    },
    vScrollerRef: scrollerRef,
  });

  usePdfWheelZoom({
    baseWidth: fitWidth,
    contentRef: pdfContainerRef,
    maxZoom,
    onZoomChange,
    scrollerRef,
    zoom,
  });

  const measureFitWidth = () => {
    const content = pdfContainerRef.current;

    if (!content || zoomRef.current !== 1) return;

    content.style.maxWidth = "";
    content.style.width = "";

    setFitWidth(content.getBoundingClientRect().width);
  };

  useEffect(() => {
    const content = pdfContainerRef.current;

    if (!content || !fitWidth) return;

    content.style.maxWidth = "none";
    content.style.width = `${fitWidth * zoom}px`;
  }, [fitWidth, pdfContainerRef, zoom]);

  const setImgsRef = (id: string, element: HTMLImageElement | null) => {
    if (element) {
      imgRefs.current.set(id, element);
    } else {
      imgRefs.current.delete(id);
    }
  };

  useEffect(() => {
    if (page) {
      const selectedRef = imgRefs.current?.get((page - 1).toString());

      if (selectedRef) {
        setTimeout(() => {
          selectedRef.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    }
  }, [page]);

  return (
    <div className="h-full overflow-auto overscroll-contain" ref={scrollerRef}>
      {imageUrls.length === 0 ? (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ">
          <Loader className="bg-bg-transparent" />
        </div>
      ) : null}
      <div className="flex">
        <div className="hidden lg:flex w-40 h-screen flex-col items-center overflow-auto fixed top-20">
          {imageUrls.map((url, index) => (
            <div
              className={`${page === index + 1 ? "bg-gray-400" : ""} cursor-pointer`}
              key={url + "-" + index}
              onClick={() => setPage(index + 1)}
            >
              <img
                alt={`Page ${index + 1}`}
                className="m-2 w-30"
                key={index}
                src={url}
              />
            </div>
          ))}
        </div>
        <div className="lg:pl-40 flex-1 min-w-0">
          <div
            className="pt-1 mx-auto w-fit max-w-full"
            data-testid="pdf-viewer-pages"
            ref={pdfContainerRef}
          >
            {imageUrls.map((url, index) => (
              <img
                alt={`Page ${index + 1}`}
                className="pl-2 pr-2 pt-1 pb-1 h-auto w-full"
                key={index}
                onLoad={measureFitWidth}
                ref={(el) => setImgsRef(index.toString(), el)}
                src={url}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
